<template>
  <div :key="$store.getters['resource/currentResource'].key">

    <loader v-if="loading"/>

    <!-- Main fields -->
    <b-row>

      <!-- Title -->
      <b-col md="4">
        <b-input-group class="flex-column">
          <label class="d-block">{{ $t('form.title')}}
            <span class="text-danger">{{$t('form.required_star') }}</span>
          </label>
          <b-form-input
            v-model.trim="title"
            name="Title"
            class="mb-4 rounded"
          >
          </b-form-input>
        </b-input-group>
        <span class="text-danger position-absolute error"
              v-if="!$v.title.required && $v.title.$dirty">
            {{$t('form.required_field')}}
        </span>
        <span class="text-danger position-absolute error"
              v-if="!$v.title.maxLength">
            {{$t('errors.error_must_have') + " " + $v.title.$params.maxLength.max + " " + $t('errors.error_letters')}}
        </span>
      </b-col>

      <!-- Code -->
      <b-col md="4">
        <div class="mb-4">
          <label class="d-block">{{ $t('form.code')}}
            <span class="text-danger">{{$t('form.required_star') }}</span>
          </label>
          <b-input-group class="d-flex flex-row flex-nowrap mb-4 ">
            <b-form-input
              class="rounded mr-1"
              v-model.trim="code"
              name="Code"
            >
            </b-form-input>
            <b-button
              style="font-size: 0.8vw"
              class="w-25"
              size="sm"
              :block="true"
              @click="generateCode"
            >
              {{$t('button.generate_code')}}
            </b-button>
          </b-input-group>
        </div>
        <span class="text-danger position-absolute error"
              v-if="!$v.code.required && $v.code.$dirty">
                  {{$t('form.required_field')}}
        </span>
        <span class="text-danger position-absolute error"
              v-if="!$v.code.maxLength">
            {{$t('errors.error_must_have') + " " + $v.code.$params.maxLength.max + " " + $t('errors.error_letters')}}
        </span>
      </b-col>

      <!-- Discount Type-->
      <b-col md="4">
        <label class="d-block">{{$t('form.discount_type')}}</label>
        <multiselect
          v-model="discountType"
          deselectLabel=""
          selectLabel=""
          selectedLabel=""
          :multiple="false"
          label="label"
          track-by="value"
          :options="discountTypes"
          :searchable="false"
          :allow-empty="false"
        >
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>

          <template slot="singleLabel" slot-scope="{ option }">
            {{ option.label }}
          </template>
        </multiselect>
      </b-col>

      <!-- Discount -->
      <b-col md="4">

        <b-input-group class="flex-column ">
          <label class="d-block">{{ $t('form.discount') }}</label>

          <!-- In case percent-->
          <div class="mb-4 flex-row flex-nowrap d-flex" v-if="discountType.value==='percent'">
            <b-form-input
              min="0"
              max="100"
              v-model="discount"
              class="form-control w-75"
              type="number"
            >
            </b-form-input>
            <span class="input-group-text d-flex justify-content-center ml-1 w-25">%</span>
          </div>

          <!-- In case money -->
          <div class="mb-4 flex-row flex-nowrap d-flex w-100" v-else-if="discountType.value === 'amount'">
            <money 
              v-model="discount"
              v-bind="money"
              class="rounded w-75"
            />

            <!-- Currency selector -->
            <multiselect
              class="w-25 ml-1"
              v-model="currency"
              :preselect-first="true"
              deselect-label=""
              select-label=""
              selected-label=""
              :multiple="false"
              label="label"
              track-by="value"
              :options="currencyTypes"
              :searchable="false"
              :allow-empty="false"
            >
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>

              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.label }}
              </template>

            </multiselect>
          </div>
        </b-input-group>
        <span class="text-danger position-absolute error"
              v-if="(!$v.discount.minValue || !$v.discount.required || (discountType.value==='percent' && !$v.discount.integer)) && $v.discount.$dirty"
        >
            {{$t('errors.error_validation')}}
            </span>
        <span class="text-danger position-absolute error"
              v-if="discountType.value === 'amount' && !$v.currency.required && $v.discount.$dirty"
        >
            {{$t('form.required_field_currency')}}
            </span>
      </b-col>


      <!-- Activations limit with checkbox -->
      <b-col md="4" class="mb-4">
        <b-input-group class="w-50  d-inline-block">
          <label class="d-block">{{$t('form.activations_limit')}}</label>
          <b-form-input
            class="rounded"
            min="1"
            v-model="activationsLimit"
            type="number"
            :disabled="unlimited"
          >
          </b-form-input>

        </b-input-group>

        <!-- Unlimited checkbox-->
        <div class="mr-1 ml-1 d-inline-block w-25">
          <b-form-checkbox
            id="unlimited_checkbox"
            class="form-check-input ml-3"
            v-model="unlimited"
          >
            {{$t('invite.unlimited')}}
          </b-form-checkbox>
        </div>
        <span class="text-danger position-absolute error"
              v-if="!unlimited && !$v.activationsLimit.minValue && $v.activationsLimit.$dirty">
            {{$t('errors.error_validation')}}
        </span>

      </b-col>

      <!-- Date Range -->
      <b-col md="4">
        <label class="d-block">{{ $t('form.available')}}
        </label>
        <DatePickerInput
            class="mb-4"
            v-model="available"
            is-range
        />
      </b-col>


      <!-- Plan-Subscription choosing -->
      <b-col md="4" class="mb-4 ">
        <group-select
          :model="selectedPlans"
          :input="planInput"
          @select="selectPlans"
          @remove="selectPlans"
          :disabled="forAllPlans"
        >
        </group-select>

        <div class="mr-1 ml-1 d-inline-block">
          <b-form-checkbox
              id="for_all_plans_checkbox"
              class="form-check-input ml-3"
              v-model="forAllPlans"
          >
            {{$t('promo.available_plans')}}
          </b-form-checkbox>
        </div>
      </b-col>

      <!-- Events choosing -->
      <b-col md="4" class="mb-4 ">
        <label class="d-block">{{$t('form.events')}}</label>
        <multiselect
          v-model="selectedEvents"
          :loading="isSelectLoading.events"
          :placeholder="$t('transactions.type_to_search')"
          deselectLabel="X"
          selectGroupLabel=""
          selectLabel=""
          selectedLabel=""
          :multiple="true"
          track-by="title"
          label="title"
          :options="events"
          group-values="groupOptions"
          group-label="groupLabel"
          :custom-label="concatEventTitle"
          :searchable="true"
          :allow-empty="true"
          :close-on-select="false"
          @search-change="debounceLoadEvents"
          :disabled="forAllTiEvents"
        >
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>

          <template slot="singleLabel" slot-scope="{ option }" v-if="selectedEvents.length">
            {{ option.title }}
          </template>
          <template v-if="!selectedEvents.length">
            {{$t('form.empty_field')}}
          </template>

        </multiselect>

        <div class="mr-1 ml-1 d-inline-block">
          <b-form-checkbox
              id="for_all_events_checkbox"
              class="form-check-input ml-3"
              v-model="forAllTiEvents"
          >
            {{$t('promo.available_ti_events')}}
          </b-form-checkbox>
        </div>
      </b-col>

      <!-- Bundles choosing -->
      <b-col md="4" class="mb-4 ">
        <label class="d-block">{{$t('form.bundles')}}</label>
        <multiselect
          v-model="selectedBundles"
          :loading="isSelectLoading.bundles"
          :placeholder="$t('transactions.type_to_search')"
          deselectLabel="X"
          selectLabel=""
          selectedLabel=""
          :multiple="true"
          label="name"
          track-by="title"
          :options="bundles"
          :custom-label="concatIdTitle"
          :searchable="true"
          :allow-empty="true"
          :close-on-select="false"
          @search-change="debounceLoadBundles"
          :disabled="forAllBundles"
        >
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>

          <template slot="singleLabel" slot-scope="{ option }" v-if="selectedBundles.length">
            {{'#' + option.id + ' ' + option.title }}
          </template>
          <template v-if="!selectedBundles.length">
            {{$t('form.empty_field')}}
          </template>

        </multiselect>

        <div class="mr-1 ml-1 d-inline-block">
          <b-form-checkbox
              id="for_all_bundles_checkbox"
              class="form-check-input ml-3"
              v-model="forAllBundles"
          >
            {{$t('promo.available_bundles')}}
          </b-form-checkbox>
        </div>
      </b-col>


      <!-- Contents choosing -->
      <b-col md="4" class="mb-4 ">
        <label class="d-block">{{$t('form.contents')}}</label>
        <multiselect
          v-model="selectedContents"
          :loading="isSelectLoading.content"
          :placeholder="$t('transactions.type_to_search')"
          deselectLabel="X"
          selectLabel=""
          selectedLabel=""
          :multiple="true"
          label="title"
          track-by="title"
          :options="contentTypes"
          :searchable="true"
          :allow-empty="true"
          :close-on-select="false"
          @search-change="debounceLoadContents"
          :disabled="forAllContent"
        >
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>

          <template slot="singleLabel" slot-scope="{ option }" v-if="selectedContents.length">
            {{option.title }}
          </template>
          <template v-if="!selectedContents.length">
            {{$t('form.empty_field')}}
          </template>

        </multiselect>

        <div class="mr-1 ml-1 d-inline-block">
          <b-form-checkbox
              id="for_all_content_checkbox"
              class="form-check-input ml-3"
              v-model="forAllContent"
          >
            {{$t('promo.available_content')}}
          </b-form-checkbox>
        </div>
      </b-col>

      <!-- Description -->
      <b-col md="4">
        <label class="d-block">{{ $t('form.description')}}
        </label>
        <textarea
          :rows="2"
          v-model="description"
          class="form-control mb-4">
          </textarea>
      </b-col>

      <span class="text-danger position-absolute error"
            v-if="$v.purchaseSelected.$invalid && $v.purchaseSelected">
            {{$t('form.required_field_purchases')}}
      </span>

    </b-row>

    <form-buttons @submit="debounceSubmit"></form-buttons>

  </div>
</template>
<script>
  import {required, minValue, maxLength, integer} from "vuelidate/lib/validators";
  import GroupSelect from "../../components/global/groupSelect.vue";
  import CodeRain from "coderain";
  import i18n from "../../utils/i18n";
  import {debounce} from 'lodash'
  import {Money} from 'v-money'
  import DatePickerInput from "../../components/forms/fields/datePickerInput";

  const crTemplate = "##################";
  const cr = new CodeRain(crTemplate);
  const maxCodeLength = 40;
  const maxTitleLength = 25;

  export default {
    components: {DatePickerInput, GroupSelect, Money},
    middleware: 'auth',
    props: {
      params: {
        id: {
          type: Number
        }
      }
    },
    computed: {
      currencyTypes() {
        return this.$store.getters['currency/currency']
      }
    },
    data: () => {
      return {
        promo: {},
        isSelectLoading: {},
        loading: true,
        title: "",
        code: "",
        unlimited: false,
        description: "",
        selectedPlans: [],
        selectedEvents: [],
        selectedBundles: [],
        events: [],
        bundles: [],
        selectedContents: [],
        contentTypes: [],
        forAllPlans: false,
        forAllTiEvents: false,
        forAllBundles: false,
        forAllContent: false,
        money: {
          decimal: '.',
          thousands: ',',
          prefix: '',
          suffix: '',
          precision: 2,
          masked: false
        },
        purchaseSelected: true,
        available: {},
        activationsLimit: '1',
        discount: '0',
        discountType: {
          value: "amount",
          label: "amount"
        },
        discountTypes: [
          {
            value: "amount",
            label: "amount"
          },
          {
            value: "percent",
            label: "percent"
          },
        ],
        currency: null,
        planInput: {
          name: "subscriptions",
          type: "select",
          label: i18n.t('form.subscriptions_id'),
          placeholder: i18n.t('transactions.type_to_search'),
          searchable: true,
          multiple: true,
          group: true,
          options_url: "plans?limit=100",
          filter_field: 'title',
          main: false
        },
        debounceSubmit: () => {
        },
        debounceLoadEvents: () => {
        },
        debounceLoadBundles: () => {
        },
        debounceLoadContents: () => {
        },
      }

    },
    validations () {
      if (this.discountType.value == 'amount') {
        return {
          title: {
            required,
            maxLength: maxLength(maxTitleLength)
          },
          code: {
            required,
            maxLength: maxLength(maxCodeLength)
          },
          discount: {
            required,
            minValue: minValue(0)
          },
          activationsLimit: {
            required,
            minValue: minValue(1)
          },
          purchaseSelected: {
            checked: value => value
          },
          currency: {
            required
          }
        }
      } else {
        return {
          title: {
            required,
            maxLength: maxLength(maxTitleLength)
          },
          code: {
            required,
            maxLength: maxLength(maxCodeLength)
          },
          discount: {
            required,
            integer,
            minValue: minValue(0)
          },
          activationsLimit: {
            required,
            minValue: minValue(1)
          },
          purchaseSelected: {
            checked: value => value
          },
        }
      }
    },
    created() {
      this.$store.dispatch('currency/getCurrency');
      if (this.params && this.params.id) {
        this.getData(`promos/${this.params.id}`, null, true).then(({data}) => {
          this.promo = data;
        }).then(() => {
          this.setPrefillData();
        })
      }

      this.loadEvents();
      this.loadBundles();
      this.loadContents();
      this.loading = false;

      this.debounceSubmit = debounce(this.submit, 1000);
      this.debounceLoadEvents = debounce(this.loadEvents, 500);
      this.debounceLoadBundles = debounce(this.loadBundles, 500);
      this.debounceLoadContents = debounce(this.loadContents, 500);
    },
    methods: {
      selectPlans(params) {
        if (params.remove) {
          _.pull(this.selectedPlans, params.value);
        } else {
          this.selectedPlans.push(params.value);
        }
      },
      loadEvents(value) {
        this.$set(this.isSelectLoading, 'events', true);
        const params = {filter: {}};
        if (value) {
          params.filter.title = value
        }
        try {
          this.getData("ti-events?limit=100", {params}).then(({data}) => {
            this.loadEventsTickets(data.items);
            // this.events = data.items;
          }).then(() => {
            this.$set(this.isSelectLoading, 'events', false);
          })
        } catch (e) {
          console.log(e);
          this.$set(this.isSelectLoading, 'events', false);
        }
      },
      loadEventsTickets(data) {
        Promise.all(data.map(item => {
          return {
            groupLabel: item.title,
            groupOptions: item.tickets.map((ticket, index) => {
              if (!ticket.active || !ticket.active) {
                return {
                  title: ticket.title,
                  value: ticket.id,
                  $isDisabled: true
                }
              } else {
                return {
                  title: ticket.title,
                  value: ticket.id
                }
              }
            })
          }
        })).then(result => {
          this.events = result;
          this.$forceUpdate();
        })
      },
      loadBundles(value) {
        this.$set(this.isSelectLoading, 'bundles', true);
        const params = {filter: {}};
        if (value) {
          params.filter.title = value
        }
        try {
          this.getData("bundles?limit=10", {params}).then(({data}) => {
            this.bundles = data.items;
          }).then(() => {
            this.$set(this.isSelectLoading, 'bundles', false);
          })
        } catch (e) {
          console.log(e);
          this.$set(this.isSelectLoading, 'bundles', false);
        }
      },
      loadContents(value) {
        this.$set(this.isSelectLoading, 'content', true);
        const params = {filter: {}};
        if (value) {
          params.filter.title = value
        }
        try {
          this.getData("content?limit=10", {params}).then(({data}) => {
            this.contentTypes = data.items;
          }).then(() => {
            this.$set(this.isSelectLoading, 'content', false);
          })
        } catch (e) {
          console.log(e);
          this.$set(this.isSelectLoading, 'content', false);
        }
      },
      setPrefillData() {
        if (!this.params || !this.params.id) {
          return;
        }

        this.title = this.promo.title || "";
        this.code = this.promo.code || "";
        this.description = this.promo.description || "";
        if (this.promo.start_date && this.promo.end_date) {
          this.available = {
            start: new Date(this.promo.start_date),
            end: new Date(this.promo.end_date)
          };
        }
        this.activationsLimit = this.promo.activations_limit <= 0 ? '1' : this.promo.activations_limit.toString();
        this.unlimited = this.promo.activations_limit <= 0;
        if (this.promo.discount_type === 'amount') {
          this.discount = this.formatToDollars(this.promo.discount);
        } else {
          this.discount = this.promo.discount.toString()
        }
        this.discountTypes.forEach(type => {
          if (type.label === this.promo.discount_type) {
            this.discountType = {
              value: type.value,
              label: type.label
            }
          }
        });
        this.code = this.promo.code;
        this.currencyTypes.forEach(type => {
          if (type.label === this.promo.currency) {
            this.currency = {
              value: type.value,
              label: type.label
            }
          }
        });

        if (!this.promo.for_all_plans) {
          this.selectedPlans = this.promo.available_for.subscriptions.map(sub => {
            return {
              label: sub.title,
              value: sub.id
            }
          });
        } else {
          this.forAllPlans = true;
        }
        if (!this.promo.for_all_ti_events) {
          this.selectedEvents = this.promo.available_for.ti_event_tickets;
        } else {
          this.forAllTiEvents = true;
        }
        if (!this.promo.for_all_bundles) {
          this.selectedBundles = this.promo.available_for.bundles;
        } else {
          this.forAllBundles = true;
        }
        if (!this.promo.for_all_content) {
          this.selectedContents = this.promo.available_for.content;
        } else {
          this.forAllContent = true;
        }
      },
      async submit() {
        this.purchaseSelected = this.checkPurchaseSelect()
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }

        let params = {
          title: this.title,
          code: this.code,
          description: this.description,
          start_date: this.formatDate(this.available.start, "L"),
          end_date: this.formatDate(this.available.end, "L"),
          activations_limit: this.unlimited ? 0 : parseInt(this.activationsLimit),
          discount: this.discountType.value === 'percent' ? this.discount : this.formatToCents(this.discount),
          discount_type: this.discountType.value,
          currency: this.currency ? this.currency.value : null,
        };

        // set array of plans/bundles/content
        params.available_for = {};
        params.available_for.subscriptions = [];
        params.available_for.ti_event_tickets = [];
        params.available_for.bundles = [];
        params.available_for.content = [];

        if (!this.forAllPlans) {
          params.available_for.subscriptions = this.getSubscriptions();
          params.for_all_plans = false;
        } else {
          params.for_all_plans = true;
        }
        if (!this.forAllTiEvents) {
          params.available_for.ti_event_tickets = this.getEventTickets();
          params.for_all_ti_events = false;
        } else {
          params.for_all_ti_events = true;
        }
        if (!this.forAllBundles) {
          params.available_for.bundles = this.selectedBundles;
          params.for_all_bundles = false;
        } else {
          params.for_all_bundles = true;
        }
        if (!this.forAllContent) {
          params.available_for.content = this.selectedContents;
          params.for_all_content = false;
        } else {
          params.for_all_content = true;
        }

        if (this.params && this.params.id) {
          params.id = this.params.id;
        }
        this.$emit('save', params);
      },
      concatIdTitle({id, title}) {
        return `#${id} ${title}`
      },
      concatEventTitle({title}) {
        return title;
      },
      getEventTickets() {
        let result = [];
        if (this.selectedEvents.length) {
          this.selectedEvents.forEach(ticket => {
            result.push({
              id: ticket.value ? ticket.value : ticket.id,
            })
          })
        }
        return result;
      },
      getSubscriptions() {
        let result = [];
        if (this.selectedPlans.length) {
          this.selectedPlans.forEach(sub => {
            result.push({
              id: sub.value
            })
          })
        }
        return result;
      },
      generateCode() {
        this.code = cr.next();
      },
      checkPurchaseSelect() {
        return !!this.selectedPlans.length || !!this.selectedEvents.length || !!this.selectedBundles.length || !!this.selectedContents.length || this.forAllPlans || this.forAllTiEvents || this.forAllBundles || this.forAllContent;
      }
    }
  }
</script>
